import { GoogleOAuthProvider } from "@react-oauth/google";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";

import { AppConfig } from "~/AppConfig";
import { Registration } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import PasswordInput from "~/components/login/passwordInput/PasswordInput";
import TermsOfServiceNotice from "~/components/login/termsOfServiceNotice/TermsOfServiceNotice";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import Input from "~/components/ui/input/Input";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import { EMAIL_VALIDATION_REG_EX } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";
import { queueReditusConversion } from "~/tracking/reditus";
import {
  clearInviteUuid,
  clearRegistrationParams,
  getRegistrationParams,
} from "~/utils/registration/params";
import { isWhiteLabel } from "~/utils/white-label";

import styles from "./Login.module.scss";
import GoogleLoginButton from "./googleLoginButton/GoogleLoginButton";

const SignUpPage = () => {
  usePageTitle(PAGE_TITLES.createAccount);

  const params = getRegistrationParams();
  const location = useLocation();
  const { setToken } = useAuth();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<Registration>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: Registration) =>
      apiClient.registration.registrationCreate({ ...params, ...data }),
    onSuccess: () => {
      clearRegistrationParams();
      clearInviteUuid();
    },
  });

  const onSubmit = async (data: Registration) => {
    const result = await tryCatchParsed(mutateAsync(data));

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    // GA4/Ads can't auto-capture the conversion for this SPA form (route change,
    // not a page load; non-native submit), so push an explicit event on submit,
    // before the redirect. GTM wires `sign_up` to the Ads conversion + a
    // User-Provided Data tag; the email is normalized here and hashed by GTM.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "sign_up",
      email: data.email.trim().toLowerCase(),
    });

    queueReditusConversion();
    // Navigation is intentionally handled by GuestOnlyRoute (which honours
    // `location.state.from`) — matches LoginPage.
    setToken(result.data.data.access_token.key);
  };

  return (
    <>
      <FormTitle title={labels.WELCOME} />
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputsFlexWrapper}>
          <div className={styles.inputFlexWrapper}>
            <FormLabel label={labels.FIRST_NAME} />
            <Input
              errormessage={errors.first_name?.message ?? ""}
              {...register("first_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              })}
              disabled={isSubmitting}
            />
          </div>
          <div className={styles.inputFlexWrapper}>
            <FormLabel label={labels.LAST_NAME} />
            <Input
              errormessage={errors.last_name?.message ?? ""}
              {...register("last_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              })}
              disabled={isSubmitting}
            />
          </div>
        </div>
        <div className={styles.inputsWrapper}>
          <FormLabel label={labels.EMAIL} />
          <Input
            errormessage={errors.email?.message ?? ""}
            {...register("email", {
              required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
              pattern: {
                value: EMAIL_VALIDATION_REG_EX,
                message: labels.INVALID_EMAIL,
              },
            })}
            disabled={isSubmitting}
          />
        </div>
        <div className={styles.inputsWrapper}>
          <PasswordInput
            passwordError={errors.password?.message}
            // @ts-expect-error refactor password to T type
            register={register}
            label={labels.NEW_PASSWORD}
            field="password"
            // @ts-expect-error refactor password to T type
            getValues={getValues}
            disabled={isSubmitting}
          />
          <PasswordInput
            passwordError={errors.password_confirmation?.message}
            // @ts-expect-error refactor password to T type
            register={register}
            label={labels.CONFIRM_PASSWORD}
            field="password_confirmation"
            // @ts-expect-error refactor password to T type
            getValues={getValues}
            disabled={isSubmitting}
          />
        </div>
        <TermsOfServiceNotice />
        <Button fullWidth type="submit" disabled={isSubmitting}>
          {labels.SIGN_UP}
        </Button>
      </form>
      {!isWhiteLabel() && (
        <div className={styles.buttonsWrapper}>
          <div className={styles.dividerWrapper}>
            <div className={styles.divider} />
            <p className={styles.orText}>{labels.OR}</p>
            <div className={styles.divider} />
          </div>
          <GoogleOAuthProvider clientId={AppConfig.GOOGLE_AUTH_CLIENT_ID}>
            <GoogleLoginButton
              buttonText="Sign up with Google"
              disabled={isPending}
            />
          </GoogleOAuthProvider>
        </div>
      )}
      <div className={styles.footer}>
        <div className={styles.footerText}>
          {labels.ALREADY_HAVE_AN_ACCOUNT}
        </div>
        <Link
          className={styles.footerLink}
          to={routes.auth.login()}
          state={location.state}
        >
          {labels.BACK_TO_LOGIN}
        </Link>
      </div>
    </>
  );
};
export default SignUpPage;
